import * as Yup from "yup";
import { FormGroup } from "../../../components/form/models/models";
import { screeningAdvisoryText } from "./screeningAdvisoryText";

export const screeningQuestionsForm: (hideApplicationType: boolean) => FormGroup = (hideApplicationType) => ({
  type: "formGroup",
  fields: [
    {
      type: "field",
      fieldType: "radio",
      key: "kinderServiceFunded",
      label:
        "Is the kinder service funded by the Victorian government, or is it currently undergoing accreditation to become a funded kinder service?",
      validation: Yup.string().trim().required("A response is required"),
      props: {
        isRequired: true,
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No",
            value: 2,
          },
        ],
      },
    },
    {
      type: "field",
      key: "advisory1",
      fieldType: "advisory",
      hideExpression: (context, values, model) => model?.kinderServiceFunded != 2,
      props: {
        type: "warning",
        Body: () => (
          <p className="list-none pl-2 m-0">
            Please note that only kindergarten services that are funded by the Victorian government are eligible for support via the KIS program.
            Kindergarten services currently undergoing accreditation may submit an application to receive a provisional outcome. Based on your
            response, as the kindergarten service is not funded by the Victorian government or currently undergoing accreditation, an application
            cannot be submitted under the KIS program. You can find out more about kindergarten funding{" "}
            <a href="http://www.vic.gov.au/kindergarten-funding" target="_blank" title="Link opens in a new tab" rel="noreferrer">
              here
            </a>
            . <i aria-hidden="true" className="pi pi-external-link"></i>
          </p>
        ),
      },
    },
    {
      type: "formGroup",
      key: "fundedKinder",
      hideExpression: (context, values, model) => model?.kinderServiceFunded != 1,
      fields: [
        {
          type: "field",
          fieldType: "radio",
          key: "parentGuardianConsent",
          label: "Please confirm that a parent/carer/guardian of the child has been involved in and consents to the application",
          validation: Yup.string().trim().required("A response is required"),
          props: {
            isRequired: true,
            options: [
              {
                label: "Yes",
                value: 1,
              },
              {
                label: "No",
                value: 2,
              },
            ],
          },
        },
        {
          type: "field",
          key: "advisory2",
          fieldType: "advisory",
          props: {
            Body: () => (
              <p className="list-none m-0">
                In order to finalise the application, you will be later required to upload evidence of parent/carer consent in the format requested.
                Parent/carer consent needs to be recorded using the{" "}
                <a
                  href="http://www.ahaconsulting.com.au/wp-content/uploads/2025/08/KIS-privacy-notice-and-consent-form.pdf"
                  target="_blank"
                  title="Link opens in a new tab"
                  rel="noreferrer">
                  KIS privacy notice and consent form
                </a>
                . <i aria-hidden="true" className="pi pi-external-link"></i>
              </p>
            ),
            type: "fieldadv",
          },
        },
        {
          type: "field",
          key: "advisory3",
          fieldType: "advisory",
          props: {
            type: "warning",
          },
          hideExpression: (context, values, model) => model.parentGuardianConsent != 2,
          label:
            "An application cannot be submitted under the KIS program without the consent of the child's parent/carer/guardian. You cannot continue until parent/carer/guardian consent is obtained.",
        },
        {
          type: "formGroup",
          key: "consentYes",
          hideExpression: (context, values, model) => model?.parentGuardianConsent != 1,
          fields: [
            {
              type: "field",
              fieldType: hideApplicationType ? "value" : "radio",
              key: "screeningApplicationType",
              label: "What type of application is this?",
              validation: Yup.string().trim().required("A response is required"),
              props: {
                isRequired: true,
                options: [
                  {
                    label: "New",
                    value: 1,
                  },
                  {
                    label: "Appeal",
                    value: 2,
                  },
                  {
                    label: "Transfer",
                    value: 3,
                  },
                ],
              },
            },
            {
              type: "field",
              key: "advisory4",
              fieldType: "advisory",
              props: {
                type: "fieldadv",
              },
              hideExpression: () => hideApplicationType,
              label: "A transfer application should be submitted by the new kindergarten service that the child has moved to",
            },
            {
              type: "field",
              key: "advisory5",
              fieldType: "advisory",
              hideExpression: (context, values, model) => model.screeningApplicationType != 2,
              props: {
                type: "warning",
                Body: () => (
                  <div className="list-none pl-2">
                    <p className="mb-2">
                      To initiate an appeal, please open the child's previous application and navigate to the <strong>Appeal Request</strong> section
                      found at the bottom of the side panel. You will then need to click <strong>Edit</strong> on this section, provide a brief reason
                      for the appeal, and <strong>Save</strong> the section.
                    </p>
                    <p className="mb-2">
                      This will create a new appeal application, which can be identified by the title <strong>Appeal</strong> at the top of the
                      application once opened.
                    </p>
                    <p className="mb-2">
                      All information from your previous application will transfer to the appeal,{" "}
                      <u>allowing you to edit any sections you would like to provide further information.</u>
                    </p>
                    <ul className="pl-2 m-0 indentation">
                      The following is also required when submitting an appeal:
                      <li className="ml-5">A new signed privacy and consent form</li>
                      <li className="ml-5">A current KIS plan if under the Disability stream (dated within 3 months of submission)</li>
                    </ul>
                    <p className="my-2">
                      Once you have finished editing the appeal please ensure you <strong>submit</strong> the appeal application.
                    </p>
                    <p className="mb-2">
                      Suggestions for what could be provided in an appeal were included in the outcome email you received. For assistance with
                      preparing the appeal, including what new information should be provided, you should contact your KIS provider as they are best
                      placed to support you in preparing your appeal.
                    </p>
                  </div>
                ),
              },
              label: "",
            },
            {
              type: "field",
              key: "advisory6",
              fieldType: "advisory",
              hideExpression: (context, values, model) => model.screeningApplicationType != 3,
              props: {
                type: "warning",
                Body: () => (
                  <p className="list-none pl-2 m-0">
                    To lodge a transfer application, please contact the KIS support team,{" "}
                    <a href="mailto:support@KISapplications.com.au">support@KISapplications.com.au</a> or 1300 564 149. You will need to provide:{" "}
                    <br />
                    <li>- Your name and the email associated with your KIS portal account - The name of your kindergarten service</li>
                    <li>- The name of the child</li> <li> - The name of their previous kindergarten. </li>
                    Our support team will work with you to set up the transfer application, so the original and transfer applications are linked in
                    the KIS portal and the assessment team have access to all the required information. Please note you will not be able to see the
                    original application.
                  </p>
                ),
              },
              label: "",
            },
            {
              type: "field",
              fieldType: "radio",
              key: "applicationCategory",
              label: "What category is the application being made under?",
              validation: Yup.number().required("A response is required"),
              hideExpression: (context, values, model) => model.screeningApplicationType != 1,
              props: {
                isRequired: true,
                options: [
                  {
                    label: "Complex Medical Needs",
                    value: 3,
                  },
                  {
                    label: "Disability (including developmental delay)",
                    value: 1,
                  },
                  {
                    label: "Both Complex Medical Needs and Disability (including developmental delay)",
                    value: 2,
                  },
                ],
              },
            },
            {
              type: "field",
              key: "advisory7",
              fieldType: "advisory",
              hideExpression: (context, values, model) => model.screeningApplicationType != 1,
              props: {
                type: "fieldadv",
                Body: () => (
                  <div>
                    To read more about the different application types, please refer to the{" "}
                    <a
                      href="http://www.ahaconsulting.com.au/wp-content/uploads/2025/08/KIS-Program-–-Guidelines-for-the-Disability-and-Complex-Medical-Needs-streams.pdf"
                      target="_blank"
                      title="Link opens in a new tab"
                      rel="noreferrer">
                      KIS program guidelines
                    </a>{" "}
                    <i aria-hidden="true" className="pi pi-external-link"></i>
                  </div>
                ),
              },
            },

            {
              type: "field",
              fieldType: "radio",
              key: "complexMedicalNeedsCriteria",
              validation: Yup.number().required("A response is required"),
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 || (model.applicationCategory != 3 && model.applicationCategory != 2),
              props: {
                Body: () => (
                  <label className="mt-2">
                    <p>
                      To apply under the Complex Medical Needs criteria, a child must have a diagnosis that requires individualised health support/s
                      for their complex medical need/s (this does not include asthma, allergy or anaphylaxis) AND must be unable to access a funded
                      kindergarten program without provision of a high level of additional support for individual health care needs.
                    </p>
                    <p className="isRequired">Please select the type of evidence you can upload:</p>
                  </label>
                ),
                options: [
                  {
                    label: [
                      "Evidence from a medical practitioner that the child has complex medical needs which require a high level of individualised health care support during the kindergarten program",
                      <strong key="1"> AND </strong>,
                      "a risk minimisation plan",
                      <strong key="2"> AND </strong>,
                      "a medical management plan (MMP) prepared or endorsed by a medical practitioner, or medical professionals who are involved in the care/support (e.g. Diabetes Educator/Nurse) of the child in relation to their medical needs. This is to ensure that the support provided to the child and any training undertaken is in line with medical advice.",
                    ],
                    value: 1,
                  },
                  {
                    label: "I can't upload all of the above documents",
                    value: 2,
                  },
                ],
              },
            },
            {
              type: "field",
              key: "advisory8",
              fieldType: "advisory",
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 || (model.applicationCategory != 3 && model.applicationCategory != 2),
              props: {
                type: "warning",
                Body: () => (
                  <>
                    <div className="list-none pl-2 m-0">
                      Please note that applications for support under this category should not be submitted where asthma, allergy or anaphylaxis are
                      the only diagnosis. The following list provides examples of procedures for which Complex Medical Needs program funding may be
                      provided:
                      <li>-Oxygen</li>
                      <li>-Tracheostomy care</li>
                      <li>-Suction</li>
                      <li>-Tube feeding</li>
                      <li>-Ileostomy or colostomy management</li>
                      <p className="mt-1 m-0">
                        For the purpose of the KIS application, a medical practitioner is a health professional registered under the Health
                        Practitioner Regulation National Law to practice in the medical profession. These professionals include general practitioners,
                        paediatricians, registered psychologists, medical specialists, and other medical professionals trained and qualified to
                        diagnose and treat illnesses, injuries, and diseases. <br />
                        An MMP should be prepared or endorsed by a medical practitioner, or medical professionals (e.g. Diabetes Educator/Nurse) who
                        are involved in the care/support of the child in relation to their medical needs. This is to ensure that the support provided
                        to the child and any training undertaken is in line with medical advice.
                      </p>
                    </div>
                  </>
                ),
              },
              label: "",
            },
            {
              type: "formGroup",
              key: "childsDisability",
              label: "The child's disability means that in the kinder environment they are (select all that apply):",
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
              test: [
                "oneSelected",
                "A response is required",
                (value) => value.riskOfSeriousInjury || value.restrictedMovement || value.childsDisabilityNone,
              ],
              props: {
                isRequired: true,
              },
              fields: [
                {
                  type: "field",
                  key: "riskOfSeriousInjury",
                  fieldType: "checkbox",
                  label: "At significant risk of serious injury to themselves and/or others",
                  expressionProps: {
                    disabled: (context, values, model) => model.childsDisabilityNone || context.disabled,
                  },
                  props: {
                    showDisabled: true,
                  },
                },
                {
                  type: "field",
                  key: "restrictedMovement",
                  fieldType: "checkbox",
                  label: "Extremely restricted in their capacity for movement",
                  expressionProps: {
                    disabled: (context, values, model) => model.childsDisabilityNone || context.disabled,
                  },
                  props: {
                    showDisabled: true,
                  },
                },
                {
                  type: "field",
                  key: "childsDisabilityNone",
                  fieldType: "checkbox",
                  label: "None of the above",
                  expressionProps: {
                    disabled: (context, values, model) => model.restrictedMovement || model.riskOfSeriousInjury || context.disabled,
                  },
                  props: {
                    showDisabled: true,
                  },
                },
              ],
            },
            {
              type: "field",
              fieldType: "radio",
              key: "childDiagnosedDisability",
              label: "Is the child:",
              validation: Yup.number().required("A response is required"),
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
              props: {
                isRequired: true,
                options: [
                  {
                    label: [
                      "Diagnosed with a disability (including developmental delay)",
                      <strong key="1"> AND </strong>,
                      "has ongoing high support needs",
                    ],
                    value: 1,
                  },
                  {
                    label: [
                      "Undergoing assessment for a disability (including developmental delay)",
                      <strong key="2"> AND </strong>,
                      "has ongoing high support needs",
                    ],
                    value: 2,
                  },
                  {
                    label: "None of the above",
                    value: 3,
                  },
                ],
              },
            },
            {
              type: "field",
              fieldType: "radio",
              key: "typeOfDiagnosedDisabilityEvidence",
              label:
                "Please select the type of evidence you can upload to confirm the child's disability diagnosis. Only one type of evidence is required to be uploaded but you can upload other relevant evidence.",
              validation: Yup.number().required("A response is required"),
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 || model.applicationCategory == 3 || model.childDiagnosedDisability != 1,
              props: {
                isRequired: true,
                options: [
                  {
                    label:
                      "Evidence from a paediatrician, specialist medical practitioner or registered psychologist, such as a letter or a report that details the disability diagnosis",
                    value: 1,
                  },
                  {
                    label: "A current NDIS plan",
                    value: 2,
                  },
                  {
                    label:
                      "Evidence the child is eligible for Victorian government Early Childhood Intervention Services (ECIS) – Continuity of Support (CoS)-  note this is not support provided through the NDIA",
                    value: 3,
                  },
                  {
                    label: "None of the above",
                    value: 4,
                  },
                ],
              },
            },
            {
              type: "field",
              key: "advisory9",
              fieldType: "advisory",
              hideExpression: (context, values, model) =>
                model.applicationCategory == 3 ||
                model.screeningApplicationType != 1 ||
                model.childDiagnosedDisability != 1 ||
                model.typeOfDiagnosedDisabilityEvidence != 1,

              props: {
                type: "warning",
                Body: () => (
                  <p className="list-none pl-2 m-0">
                    Please note that evidence from allied health practitioners (e.g. occupational therapists, speech pathologists etc) cannot be used
                    to confirm a medical diagnosis for the KIS program.
                    <br />
                    Evidence documents must meet the requirements outlined in the KIS program guidelines.
                  </p>
                ),
              },
              label: "",
            },
            {
              type: "field",
              key: "advisory10",
              fieldType: "advisory",
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 ||
                model.applicationCategory == 3 ||
                model.childDiagnosedDisability != 1 ||
                model.typeOfDiagnosedDisabilityEvidence != 2,
              props: {
                type: "warning",
                Body: () => (
                  <p className="list-none pl-2 m-0">
                    The end date of the plan must be after this KIS application is submitted. A rolled over plan can be submitted if other evidence is
                    uploaded to confirm that the plan has been extended, such as a screenshot from the NDIA portal that shows the new end date, the
                    NDIS number and the child's name.
                    <br />
                    Evidence showing that an NDIS application has been lodged is not accepted for the KIS Program and should not be submitted.
                    <br />
                    Evidence documents must meet the requirements outlined in the KIS program guidelines.
                  </p>
                ),
              },
              label: "",
            },
            {
              type: "field",
              key: "advisory11",
              fieldType: "advisory",
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 ||
                model.applicationCategory == 3 ||
                model.childDiagnosedDisability != 1 ||
                model.typeOfDiagnosedDisabilityEvidence != 3,
              props: {
                type: "warning",
                Body: () => (
                  <p className="list-none pl-2 m-0">
                    The ECIS CoS program applies to children who would otherwise be eligible for NDIS support, but do not meet the residency
                    requirements. If the child has an NDIS plan, do not select this option.
                  </p>
                ),
              },
              label: "",
            },
            {
              type: "field",
              fieldType: "radio",
              key: "evidenceUndergoingAssessment",
              label:
                "Please select the type of evidence you can upload to confirm that the child is undergoing continuing assessment for disability. Only one evidence option is required to be uploaded but you can upload other relevant evidence.",
              validation: Yup.number().required("A response is required"),
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 || model.applicationCategory == 3 || model.childDiagnosedDisability != 2,
              props: {
                isRequired: true,
                options: [
                  {
                    label: [
                      "Option 1 An appointment letter for a disability assessment by a paediatrician, specialist medical practitioner or registered psychologist",
                      <strong key="1"> AND </strong>,
                      "Supporting documentation from a medical professional outlining the child’s development and confirming that the child has been referred for assessment of disability",
                    ],
                    value: 1,
                  },
                  {
                    label:
                      "Option 2 Documentation from a paediatrician, specialist medical practitioner or registered psychologist showing that they will have further contact with the child for the purpose of ongoing assessment for a disability",
                    value: 2,
                  },
                  {
                    label: "None of the above",
                    value: 3,
                  },
                ],
              },
            },
            {
              type: "field",
              key: "advisory12",
              fieldType: "advisory",
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 || model.applicationCategory == 3 || model.childDiagnosedDisability != 2,
              props: {
                type: "warning",
                Body: () => (
                  <div className="list-none pl-2 m-0">
                    Please note that:
                    <li>
                      -pending appointments for assessment with allied health professionals (e.g. occupational therapist, speech pathologist) cannot
                      be accepted
                    </li>
                    <li>
                      -the documents must show that the appointment is for ongoing assessment for disability, rather than general checkup appointments
                    </li>
                    <li>
                      -if the child has an <strong>NDIS plan</strong> as evidence of disability or developmental delay, select the
                      <i>
                        Diagnosed with a disability (including developmental delay) <strong>AND</strong> has ongoing high support needs
                      </i>
                      option from the list above
                    </li>
                  </div>
                ),
              },
              label: "",
            },

            {
              type: "field",
              fieldType: "radio",
              key: "kisPlanCompleted",
              label: "Has a KIS plan for the child been completed or updated within the last 3 months?",
              validation: Yup.number().required("A response is required"),
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
              props: {
                isRequired: true,
                options: [
                  {
                    label: "Yes",
                    value: 1,
                  },
                  {
                    label: "No",
                    value: 2,
                  },
                ],
              },
            },

            {
              type: "field",
              key: "advisory13",
              fieldType: "advisory",
              hideExpression: (context, values, model) =>
                model.screeningApplicationType != 1 || (model.applicationCategory != 1 && model.applicationCategory != 2),
              props: {
                type: "fieldadv",
                Body: () => (
                  <div>
                    {" "}
                    Please note that you will be required to upload a copy of a{" "}
                    <a
                      href="https://www.ahaconsulting.com.au/wp-content/uploads/2025/08/KIS-plan.pdf"
                      target="_blank"
                      title="Link opens in a new tab"
                      rel="noreferrer">
                      KIS plan
                    </a>{" "}
                    <i aria-hidden="true" className="pi pi-external-link"></i> dated within the last 3 months in order to submit an application for
                    support under the disability stream of the KIS program.
                  </div>
                ),
              },
              label: "",
            },

            ...screeningAdvisoryText,
          ],
        },
      ],
    },
  ],
});
